import type { ShareLink, ShareListRequest } from '@lista/shared-validation';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useCore } from '../context';
import { queryKeys } from '../query-keys';
import type { MembersOverview } from '../services';

export function useMembers(listId: string, enabled = true) {
  const { services } = useCore();
  return useQuery({
    queryKey: queryKeys.lists.members(listId),
    queryFn: () => services.sharing.members(listId),
    enabled,
  });
}

function useInvalidateSharing(listId: string) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: queryKeys.lists.members(listId) });
    void qc.invalidateQueries({ queryKey: queryKeys.lists.detail(listId) });
    void qc.invalidateQueries({ queryKey: queryKeys.lists.dashboards, refetchType: 'none' });
  };
}

export function useAddMember(listId: string) {
  const { services } = useCore();
  const invalidate = useInvalidateSharing(listId);
  return useMutation({
    mutationFn: (body: ShareListRequest) => services.sharing.add(listId, body),
    onSuccess: invalidate,
  });
}

function useOptimisticMembers(listId: string) {
  const qc = useQueryClient();
  return {
    patch(update: (m: MembersOverview) => MembersOverview) {
      const before = qc.getQueryData<MembersOverview>(queryKeys.lists.members(listId));
      qc.setQueryData<MembersOverview>(queryKeys.lists.members(listId), (m) => (m ? update(m) : m));
      return { before };
    },
    restore(before: MembersOverview | undefined) {
      qc.setQueryData(queryKeys.lists.members(listId), before);
    },
  };
}

/** Optimistic role change. */
export function useChangeRole(listId: string) {
  const { services } = useCore();
  const invalidate = useInvalidateSharing(listId);
  const members = useOptimisticMembers(listId);
  return useMutation({
    mutationFn: ({ userId, role }: { userId: string; role: 'EDITOR' | 'VIEWER' }) =>
      services.sharing.changeRole(listId, userId, role),
    onMutate: ({ userId, role }) =>
      members.patch((m) => ({
        ...m,
        members: m.members.map((x) => (x.userId === userId ? { ...x, role } : x)),
      })),
    onError: (_e, _v, ctx) => members.restore(ctx?.before),
    onSettled: invalidate,
  });
}

/** Optimistic removal (owner removes someone, or a member leaves). */
export function useRemoveMember(listId: string) {
  const { services } = useCore();
  const invalidate = useInvalidateSharing(listId);
  const members = useOptimisticMembers(listId);
  return useMutation({
    mutationFn: (userId: string) => services.sharing.remove(listId, userId),
    onMutate: (userId) =>
      members.patch((m) => ({ ...m, members: m.members.filter((x) => x.userId !== userId) })),
    onError: (_e, _v, ctx) => members.restore(ctx?.before),
    onSettled: invalidate,
  });
}

export function useRevokeInvitation(listId: string) {
  const { services } = useCore();
  const invalidate = useInvalidateSharing(listId);
  const members = useOptimisticMembers(listId);
  return useMutation({
    mutationFn: (invitationId: string) => services.sharing.revokeInvitation(listId, invitationId),
    onMutate: (invitationId) =>
      members.patch((m) => ({
        ...m,
        invitations: m.invitations.filter((x) => x.id !== invitationId),
      })),
    onError: (_e, _v, ctx) => members.restore(ctx?.before),
    onSettled: invalidate,
  });
}

/** Lists other people shared with me that I haven't accepted yet ("Shared with me"). */
export function usePendingShares() {
  const { services } = useCore();
  return useQuery({
    queryKey: queryKeys.shares.pending,
    queryFn: services.sharing.pending,
    refetchOnWindowFocus: true,
  });
}

/** Decline a pending share: it disappears from "Shared with me". */
export function useDeclineShare() {
  const { services } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (listId: string) => services.sharing.declineShare(listId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.shares.pending });
      void qc.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}

/** Accept a pending share: the list moves from "pending" to my lists. */
export function useAcceptShare() {
  const { services } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (listId: string) => services.sharing.acceptShare(listId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.shares.pending });
      void qc.invalidateQueries({ queryKey: queryKeys.lists.all });
      void qc.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}

/** Owner: the list's public links (active and expired, not revoked). */
export function useShareLinks(listId: string, enabled = true) {
  const { services } = useCore();
  return useQuery({
    queryKey: queryKeys.lists.shareLinks(listId),
    queryFn: () => services.sharing.shareLinks(listId),
    enabled,
  });
}

export function useCreateShareLink(listId: string) {
  const { services } = useCore();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (expiresInDays: number) => services.sharing.createShareLink(listId, expiresInDays),
    onSuccess: () => void qc.invalidateQueries({ queryKey: queryKeys.lists.shareLinks(listId) }),
  });
}

/** Optimistic: a revoked link disappears at once; rolled back if the API refuses. */
export function useRevokeShareLink(listId: string) {
  const { services } = useCore();
  const qc = useQueryClient();
  const key = queryKeys.lists.shareLinks(listId);
  return useMutation({
    mutationFn: (linkId: string) => services.sharing.revokeShareLink(listId, linkId),
    onMutate: (linkId) => {
      const before = qc.getQueryData<ShareLink[]>(key);
      qc.setQueryData<ShareLink[]>(key, (links) => links?.filter((l) => l.id !== linkId));
      return { before };
    },
    onError: (_e, _v, ctx) => qc.setQueryData(key, ctx?.before),
    onSettled: () => void qc.invalidateQueries({ queryKey: key }),
  });
}

/** Anonymous page: the shared list, refreshed every few seconds while the tab is visible. */
export const PUBLIC_LIST_POLL_MS = 5000;
export function usePublicList(token: string) {
  const { services } = useCore();
  return useQuery({
    queryKey: queryKeys.publicList(token),
    queryFn: () => services.sharing.publicList(token),
    refetchInterval: (q) => (q.state.status === 'error' ? false : PUBLIC_LIST_POLL_MS),
    refetchOnWindowFocus: true,
    retry: false,
  });
}
